import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

// import { ReactComponent as DeleteIcon } from '../../../assets/icons/delete-icon.svg';
// import { ReactComponent as EyeIcon } from '../../../assets/icons/eye-2-icon.svg';
import { ReactComponent as AlertIcon } from '../../../assets/icons/tables/alert-icon.svg';
import { ReactComponent as DocumentIcon } from '../../../assets/icons/tables/document-file.svg';
import { ReactComponent as Location } from '../../../assets/icons/tables/location.svg';
import DefaultApiLimit from '../../../config/defaultLimit';
import {
  ChargingStationInterFace,
  RequestChargingStationInterface,
} from '../../../interface/charging-station.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import {
  disableRejectedStationManagementApplied,
  fetchAllRejectedStation,
  fetchRejectedStationManagementMethodFilters,
  updateRejectedStationManagementFilters,
  setSortData,
} from '../../../redux/slices/RejectedStationManagementSlice';
import { setSelectedStation } from '../../../redux/slices/StationManagementSidePanelSlice';
import { fetchPendingNotification } from '../../../redux/slices/UnapprovedStationManagementSlice';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import StationSidePanel from '../../../shared-components/table-side-panel/StationSidePanel';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import ConvertUTCtoHHMM from '../../../shared-functions/ConvertUTCtoHHMM';
import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
import FormatUtcString from '../../../shared-functions/FormatUtcString';
import chargingStationApi from '../../../utils/api/charging-station';

/**
 * Rejected charging station list function component
 */
function RejectedStationList({
  handleDeleteStationWarning,
}: {
  handleDeleteStationWarning: (value: string | undefined) => void;
}) {
  const { t } = useTranslation('station');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    stations,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortField,
    sortBy,
  } = useAppSelector((state) => state.rejectedStation);
  const [modalView, setModalView] = useState({
    status: false,
    title: '',
    header: true,
    content: <div />,
    rightCorner: '',
  });
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [searchParameters, setSearchParameters] = useSearchParams();
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);

  /**
   * Function to open device view model
   */
  const handleOpenModelView = (chargingStation: ChargingStationInterFace, newTab: boolean) => {
    if (chargingStation) {
      dispatch(
        setSelectedStation({
          id: chargingStation?._id,
          name: chargingStation?.name,
        }),
      );
      if (newTab) {
        const url = `/ev-admin/charging-station/rejected/station/${chargingStation._id}/station-dashboard?searchBy=${chargingStation.name}`;
        window.open(url, '_blank'); // opens in a new tab
      } else {
        navigate({
          pathname: `/ev-admin/charging-station/rejected/station/${chargingStation._id}/station-dashboard`,
          search: `?searchBy=${chargingStation.name}`,
        });
      }
    } else {
      console.log('Error while opening device in separate page');
    }
  };

  /**
   * View the charging station details
   */
  // const viewChargingStation = (stationId: String) => {
  //   const stationDetails = stations.find(
  //     (value: ChargingStationInterFace) => value.id === stationId,
  //   );
  //   if (stationDetails && Object.keys(stationDetails).length > 0) {
  //     viewFunction(stationDetails);
  //   }
  // };
  const column = [
    {
      label: t('stationName'),
      accessorKey: 'name' as keyof ChargingStationInterFace,
      sortAllow: true,
      queryParamsKey: 'stationId',
      sidePanelComponent: (
        <StationSidePanel
          fetchIdFrom="queryParams"
          usageCostView="rejected"
          onDeleteStation={handleDeleteStationWarning}
        />
      ),
      handleClickExpandView: handleOpenModelView,
    },
    {
      label: t('facilityName'),
      accessorKey: 'facilityName' as keyof ChargingStationInterFace,
      sortAllow: true,
      /**
       * Custom data for facility name
       * @param row
       */
    },
    {
      label: t('facilityAccountId'),
      accessorKey: 'accountCustomId' as keyof ChargingStationInterFace,
      sortAllow: true,
      /**
       * Facility name view in table
       * @param row
       * @returns
       */
    },
    {
      label: t('landDocument'),
      accessorKey: 'landOwnerShipDocument' as keyof ChargingStationInterFace,
      /**
       * Document view in table
       * @param station
       * @param index
       */
      customData: (station: ChargingStationInterFace) =>
        station?.landOwnerShipDocument?.length ? (
          <div className="document-in-station-table">
            <div className="document-container">
              <span>
                <DocumentIcon />
              </span>
              <p className="table-row-each-data">{station?.landOwnerShipDocument?.[0]?.fileName}</p>
            </div>
            {station?.landOwnerShipDocument?.length > 1 && (
              <div className="document-count-wrap">
                <p className="table-row-each-data">+{station.landOwnerShipDocument.length - 1}</p>
              </div>
            )}
          </div>
        ) : null,
      hideWhenSidePanelOpen: true,
    },
    {
      label: t('ebReports'),
      accessorKey: 'ebReport' as keyof ChargingStationInterFace,
      /**
       * Document view in table
       * @param station
       * @param index
       */
      customData: (station: ChargingStationInterFace) =>
        station?.ebReport?.length ? (
          <div className="document-in-station-table">
            <div className="document-container">
              <span>
                <DocumentIcon />
              </span>
              <p className="table-row-each-data">{station?.ebReport?.[0]?.fileName}</p>
            </div>
            {station?.ebReport?.length > 1 && (
              <div className="document-count-wrap">
                <p className="table-row-each-data">+{station.ebReport.length - 1}</p>
              </div>
            )}
          </div>
        ) : null,
      hideWhenSidePanelOpen: true,
    },
    {
      label: t('electricityType'),
      accessorKey: 'electricityContractType' as keyof ChargingStationInterFace,
      sortAllow: false,
    },
    {
      label: t('operatingHrs'),
      accessorKey: 'opensAt' as keyof ChargingStationInterFace,
      sortAllow: false,
      /**
       * Time view in table
       * @param station
       * @param index
       */
      customData: (station: ChargingStationInterFace) =>
        station.opensAt && station.closesAt ? (
          <div className="time-in-station-table">
            <p className="table-row-each-data">{`${station.opensAt} - ${station.closesAt}`}</p>
          </div>
        ) : null,
      hideWhenSidePanelOpen: true,
    },
    {
      label: t('stationLocation'),
      accessorKey: 'address' as keyof ChargingStationInterFace,
      /**
       * custom data function
       * @param currentRow
       */
      customData: (currentRow: ChargingStationInterFace) =>
        currentRow.address ? (
          <div className="tag-default-label">
            <Location /> <span>{currentRow.address}</span>
          </div>
        ) : (
          <div />
        ),
    },
    {
      label: t('media'),
      accessorKey: 'medias' as keyof ChargingStationInterFace,
      /**
       * Media view in table
       * @param station
       * @param index
       */
      customData: (station: ChargingStationInterFace) =>
        station.medias ? (
          <div className="medias-in-station-table">
            {(() => {
              let selectedFilesView;
              if (station.medias && station.medias?.length <= 4) {
                selectedFilesView = (
                  <div className="selected-files-view-wrap">
                    {station.medias?.map((media) => (
                      <div className="single-selected-wrap">
                        {(() => {
                          let fileTypeView;
                          if (media?.fileType?.includes('image')) {
                            fileTypeView = (
                              <img src={media.signedUrl} alt="" className="selected-image" />
                            );
                          } else if (media?.fileType?.includes('video')) {
                            fileTypeView = (
                              <video controls={false} className="selected-video" preload="none">
                                <source src={media.signedUrl} type="video/mp4" />
                                <track
                                  src="path/to/captions.vtt"
                                  kind="captions"
                                  label="English captions"
                                  default
                                />
                                {t('browserNotSupport')}{' '}
                              </video>
                            );
                          }
                          return fileTypeView;
                        })()}
                      </div>
                    ))}
                  </div>
                );
              } else if (station.medias && station.medias?.length > 4) {
                selectedFilesView = (
                  <div className="selected-files-view-wrap">
                    {station.medias.slice(0, 3).map((media) => (
                      <div className="single-selected-wrap">
                        {(() => {
                          let fileTypeView;
                          if (media?.fileType?.includes('image')) {
                            fileTypeView = (
                              <img src={media.signedUrl} alt="" className="selected-image" />
                            );
                          } else if (media?.fileType?.includes('video')) {
                            fileTypeView = (
                              <video controls={false} className="selected-video" preload="none">
                                <source src={media.signedUrl} type="video/mp4" />
                                <track
                                  src="path/to/captions.vtt"
                                  kind="captions"
                                  label="English captions"
                                  default
                                />
                                {t('browserNotSupport')}{' '}
                              </video>
                            );
                          }
                          return fileTypeView;
                        })()}
                      </div>
                    ))}
                    <div className="single-selected-wrap">
                      <p className="number-of-files">+{station.medias.length - 3}</p>
                    </div>
                  </div>
                );
              }
              return selectedFilesView;
            })()}
          </div>
        ) : null,
      hideWhenSidePanelOpen: true,
    },
    {
      label: t('rejectReason'),
      accessorKey: 'status' as keyof ChargingStationInterFace,
      /**
       * custom data function
       * @param currentRow
       */
      customData: (currentRow: ChargingStationInterFace) => (
        <div className="table-alert-tag">
          <div className="table-alert-content">
            <AlertIcon />
            <span>{currentRow.submission.reason}</span>
          </div>
        </div>
      ),
    },
    // {
    //   label: t('actions'),
    //   accessorKey: '' as keyof ChargingStationInterFace,
    //   columnWidth: 90,
    //   customData:
    //     /**
    //      * Function for action buttons
    //      * @param station
    //      * @returns
    //      */
    //     (station: ChargingStationInterFace) => (
    //       <div className="action-button-group">
    //         <div className="action-button-item view-color">
    //           <EyeIcon onClick={() => viewChargingStation(station.id)} />
    //         </div>
    //         <div className="action-button-item view-color">
    //           <DeleteIcon onClick={() => handleDeleteStationWarning(station.id)} />
    //         </div>
    //       </div>
    //     ),
    // },
  ];

  /**
   * Column defination for the table
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('stationName'), accessorKey: 'name' },
    { label: t('facilityName'), accessorKey: 'facilityName' },
    { label: t('landDocument'), accessorKey: 'landOwnerShipDocument' },
    { label: t('ebReports'), accessorKey: 'ebReport' },
    { label: t('electricityType'), accessorKey: 'electricityContractType' },
    { label: t('operatingHrs'), accessorKey: 'opensAt' },
    { label: t('stationLocation'), accessorKey: 'address' },
    { label: t('rejectReason'), accessorKey: 'status' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Handle the fetch the data in api function
   * @param data
   */
  const fetchData = (data: RequestChargingStationInterface) => {
    if (!loading) {
      dispatch(fetchAllRejectedStation(data));
    }
  };

  /**
   * Close modal function
   */
  const closeModal = () => {
    setModalView({
      status: false,
      title: '',
      header: false,
      content: <div />,
      rightCorner: '',
    });
  };

  /**
   * Table pagination function
   * @param value
   */
  const pageNateLimitChange = (value: RequestChargingStationInterface) => {
    const queryParameters = GetParametersFromCommonFilters(filters);

    fetchData({ ...queryParameters, ...value, sortField, sortBy });
  };

  /**
   * Export data function
   */
  const exportDataPdfOrCSV = (type: string) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    setExportLoader(true);
    const payload = {
      submissionStatus: 'rejected',
      ...queryParameters,
    };
    if (sortBy) payload.sortBy = sortBy;
    if (sortField) payload.sortField = sortField;
    chargingStationApi
      .exportChargingStations(payload)
      .then((response) => {
        // Convert data to CSV format
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.map((row: ChargingStationInterFace, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;

              case 'name':
                return row.name || 'N/A';

              case 'facilityName':
                return row.facilityName || 'N/A';

              case 'landOwnerShipDocument':
                return row.landOwnerShipDocument?.[0]?.fileName || 'N/A';

              case 'ebReport':
                return row.ebReport?.[0]?.fileName || 'N/A';

              case 'electricityContractType':
                return row.electricityContractType || 'N/A';

              case 'operatingHrs':
                return row.opensAt && row.closesAt
                  ? `${FormatUtcString(ConvertUTCtoHHMM(row.opensAt))} - ${FormatUtcString(
                      ConvertUTCtoHHMM(row.closesAt),
                    )}`
                  : 'N/A';

              case 'address':
                return row.address || 'N/A';

              case 'rejectReason':
                return row.submission?.reason || 'N/A';

              default:
                return (row as any)[col.accessorKey] || 'N/A';
            }
          }),
        );
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'charging-station-rejected.csv');
          } else {
            exportPDF(headers, rows, 'charging-station-rejected.pdf');
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Handle the filter data clear function
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * handleOpenStationSidePanel
   */
  const handleOpenStationSidePanel = (id: string) => {
    searchParameters.set('stationId', id);
    setSearchParameters(searchParameters);
  };
  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, page, limit, sortField, sortBy });
  };

  useEffect(() => {
    if (isFilterUpdated || stations.length === 0) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchData({
        page: DefaultApiLimit.page,
        limit,
        ...queryParameters,
      });
      dispatch(disableRejectedStationManagementApplied());
      dispatch(fetchPendingNotification());
    }
  }, [filters]);

  return (
    <>
      <ModalBox
        open={modalView.status}
        title={modalView.title}
        content={modalView.content}
        closeModal={closeModal}
        header={modalView.header}
        rightCorner={modalView.rightCorner}
      />
      <CustomTable
        dataKey="StationRejected"
        version="v2"
        header={column}
        data={stations}
        serialNo
        actionButton
        limit={limit}
        height={window.innerHeight >= 700 ? 335 : 344}
        loading={loading}
        page={page}
        totalPages={totalPages}
        totalResults={totalResults}
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={pageNateLimitChange}
        onHiddenColumnsChange={setHiddenColumns}
        sizeChangeButtonRequired
        filters={filters}
        filtersToDispatch={updateRejectedStationManagementFilters}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchRejectedStationManagementMethodFilters}
        refreshRequired
        handleRefresh={handleRefresh}
        ReferenceCurrentPages={handleRefresh}
        exportDataPdfOrCSV={exportDataPdfOrCSV}
        exportLoader={exportLoader}
        hoverLabel="Refresh"
        rowClickEvent={handleOpenStationSidePanel}
        rowClickSidePanel={
          <StationSidePanel
            fetchIdFrom="queryParams"
            usageCostView="rejected"
            onDeleteStation={handleDeleteStationWarning}
          />
        }
        rowClickParamsKey="stationId"
        explore={false}
      />
    </>
  );
}

export default RejectedStationList;
